import { Download, FileText } from 'lucide-react';

type FileData = {
  selectedFiles: Array<{
    key?: string;
    name?: string;
    path?: string;
    type?: string;
    field?: string;
    description?: string;
  }>;
};

interface LegalStatusProps {
  data: {
    f1: FileData;
    f2: FileData;
    section1: {
      byLaws: {
        page: string;
        paragraph: string;
      };
      charter: {
        page: string;
        paragraph: string;
      };
      doesDocumentDefines: string;
      articlesOfIncorporation: {
        page: string;
        section: string;
        paragraph: string;
      };
    };
  };
  onDownload?: (key: string) => void;
}

function isFileData(value: any): value is FileData {
  return value && 'selectedFiles' in value;
}

export default function LegalStatus({ data, onDownload }: LegalStatusProps) {
  if (!data) return null;

  // Filter and cast the entries to ensure they are FileData type
  const documentFields = Object.entries(data).filter(([key, value]) => key.startsWith('f') && isFileData(value));

  return (
    <section aria-labelledby='legal-status-title' className='mt-8'>
      <div className='bg-white sm:rounded-lg'>
        <div className='py-5'>
          <div className='px-4 py-2 sm:px-6 border-l-4 border-gray-900'>
            <div className='flex items-center'>
              <FileText className='w-6 h-6 text-gray-900 mr-2' />
              <h2 id='legal-status-title' className='m-0 text-lg leading-6 font-medium text-gray-900'>
                Step 3: Legal Status
              </h2>
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>Documents</h3>
          <div className='space-y-3'>
            {documentFields.map(([key, value]) => {
              const fileData = value as FileData;
              const fileKey = fileData.selectedFiles?.[0]?.key;
              return (
                fileKey && (
                  <div key={key} className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'>
                    <div className='flex items-center'>
                      <FileText className='w-5 h-5 text-gray-900 mr-2' />
                      <span className='text-sm text-gray-900'>Document {key.toUpperCase()}</span>
                    </div>
                    {onDownload && (
                      <button
                        onClick={() => onDownload(fileKey)}
                        className='flex items-center px-3 py-1 text-sm text-gray-700 hover:text-gray-900'
                      >
                        <Download className='w-4 h-4 mr-1' />
                        Download
                      </button>
                    )}
                  </div>
                )
              );
            })}
          </div>
        </div>

        {/* Section 1 Details */}
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>Document Details</h3>
          <div className='space-y-4'>
            <div className='grid grid-cols-1 gap-4'>
              {/* Articles of Incorporation */}
              <div className='bg-gray-50 p-4 rounded-lg'>
                <h4 className='font-medium text-gray-900 mb-2'>Articles of Incorporation</h4>
                <div className='grid grid-cols-3 gap-4 text-sm'>
                  <div>
                    <span className='text-gray-500'>Page:</span>
                    <span className='ml-2'>{data.section1.articlesOfIncorporation.page || 'N/A'}</span>
                  </div>
                  <div>
                    <span className='text-gray-500'>Section:</span>
                    <span className='ml-2'>{data.section1.articlesOfIncorporation.section || 'N/A'}</span>
                  </div>
                  <div>
                    <span className='text-gray-500'>Paragraph:</span>
                    <span className='ml-2'>{data.section1.articlesOfIncorporation.paragraph || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* By Laws */}
              <div className='bg-gray-50 p-4 rounded-lg'>
                <h4 className='font-medium text-gray-900 mb-2'>By Laws</h4>
                <div className='grid grid-cols-2 gap-4 text-sm'>
                  <div>
                    <span className='text-gray-500'>Page:</span>
                    <span className='ml-2'>{data.section1.byLaws.page || 'N/A'}</span>
                  </div>
                  <div>
                    <span className='text-gray-500'>Paragraph:</span>
                    <span className='ml-2'>{data.section1.byLaws.paragraph || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Charter */}
              <div className='bg-gray-50 p-4 rounded-lg'>
                <h4 className='font-medium text-gray-900 mb-2'>Charter</h4>
                <div className='grid grid-cols-2 gap-4 text-sm'>
                  <div>
                    <span className='text-gray-500'>Page:</span>
                    <span className='ml-2'>{data.section1.charter.page || 'N/A'}</span>
                  </div>
                  <div>
                    <span className='text-gray-500'>Paragraph:</span>
                    <span className='ml-2'>{data.section1.charter.paragraph || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Document Defines */}
              <div className='bg-gray-50 p-4 rounded-lg'>
                <h4 className='font-medium text-gray-900 mb-2'>Document Defines</h4>
                <div className='text-sm'>
                  <span className='text-gray-500'>Type:</span>
                  <span className='ml-2'>{data.section1.doesDocumentDefines || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
